import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Facebook, Twitter, Linkedin, Instagram, Mail, Phone, MapPin, X } from 'lucide-react';
import { partnerLogos } from '../data/partners';
import PartnerLogo from './PartnerLogo';

const Footer = () => {
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const currentYear = new Date().getFullYear();

  const footerSections = [
    {
      title: 'Company',
      links: [
        { name: 'About Us', path: '/about' }
      ]
    },
    {
      title: 'Services',
      links: [
        { name: 'Investment Banking', path: '/services#investment' },
        { name: 'Asset Management', path: '/services#asset' },
        { name: 'Corporate Finance', path: '/services#corporate' },
        { name: 'Risk Management', path: '/services#risk' },
        { name: 'Financial Inclusion', path: '/services#inclusion' },
        { name: 'Food Security', path: '/services#food-security' },
        { name: 'Infrastructure Development', path: '/services#infrastructure' }
      ]
    },
    {
      title: 'Support',
      links: [
        { name: 'Contact Us', path: '/contact' },
        { name: 'Privacy Policy', path: '/privacy' }
      ]
    }
  ];

  const socialLinks = [
    { icon: Facebook, href: 'https://facebook.com', label: 'Facebook' },
    { icon: Twitter, href: 'https://twitter.com', label: 'Twitter' },
    { icon: Linkedin, href: 'https://linkedin.com', label: 'LinkedIn' },
    { icon: Instagram, href: 'https://instagram.com', label: 'Instagram' }
  ];

  const contactInfo = [
    { icon: Mail, text: 'financier4@yahoo.com, franciskontoh4@gmail.com' },
    { icon: Phone, text: '+233 200738204, +233 256895006' },
    { icon: MapPin, text: 'PEDUASE, EASTERN REGION' }
  ];

  const handleLinkClick = (e, path) => {
    if (path === '/privacy') {
      e.preventDefault();
      setShowPrivacyModal(true);
      return;
    }
    if (path.startsWith('http')) {
      e.preventDefault();
      alert('This page is under development. Please contact us for more information.');
    }
  };

  return (
    <>
    <footer className="bg-gradient-to-br from-gray-900 to-gray-800 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Company Info */}
          <div className="lg:col-span-2">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <div className="flex items-center space-x-2 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-primary-600 to-accent-600 rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold text-xl">AFB</span>
                </div>
                <h3 className="text-xl font-bold">AfricanFinancial Bridge Limited</h3>
              </div>
              <p className="text-gray-300 mb-6 leading-relaxed">
                Connecting global markets with innovative financial solutions. 
                We bridge the gap between opportunity and success through 
                strategic investment and comprehensive financial services.
              </p>
              
              {/* Contact Info */}
              <div className="space-y-3">
                {contactInfo.map((contact, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className="flex items-center space-x-3 text-gray-300"
                  >
                    <contact.icon className="w-4 h-4 text-primary-400" />
                    <span className="text-sm">{contact.text}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>

          {/* Footer Links */}
          {footerSections.map((section, sectionIndex) => (
            <motion.div
              key={section.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: sectionIndex * 0.1 }}
              viewport={{ once: true }}
            >
              <h4 className="text-lg font-semibold mb-4 text-white">
                {section.title}
              </h4>
              <ul className="space-y-2">
                {section.links.map((link, linkIndex) => (
                  <motion.li
                    key={link.name}
                    initial={{ opacity: 0, x: -10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.4, delay: (sectionIndex * 0.1) + (linkIndex * 0.05) }}
                    viewport={{ once: true }}
                  >
                    <Link
                      to={link.path}
                      onClick={(e) => handleLinkClick(e, link.path)}
                      className="text-gray-300 hover:text-primary-400 transition-colors duration-200 text-sm"
                    >
                      {link.name}
                    </Link>
                  </motion.li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>

        {/* Working with these companies */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
          className="border-t border-gray-700 mt-10 pt-8"
        >
          <p className="text-center text-gray-300 text-base font-semibold mb-10">Working with these companies</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4 md:gap-5 max-w-6xl mx-auto px-2">
            {partnerLogos.map((partner) => (
              <PartnerLogo key={partner.alt} partner={partner} size="footer" variant="card" />
            ))}
          </div>
        </motion.div>

        {/* Bottom Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          viewport={{ once: true }}
          className="border-t border-gray-700 mt-12 pt-8"
        >
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="text-gray-400 text-sm">
              © {currentYear} Financial Bridge. All rights reserved.
            </div>
            
            {/* Social Links */}
            <div className="flex space-x-4">
              {socialLinks.map((social, index) => (
                <motion.a
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ scale: 1.1, y: -2 }}
                  whileTap={{ scale: 0.95 }}
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="w-10 h-10 bg-gray-800 hover:bg-primary-600 rounded-full flex items-center justify-center transition-all duration-300"
                  aria-label={social.label}
                >
                  <social.icon className="w-5 h-5" />
                </motion.a>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </footer>

      <AnimatePresence>
        {showPrivacyModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
            onClick={() => setShowPrivacyModal(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ duration: 0.2 }}
              className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[85vh] flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between p-6 border-b border-gray-200 sticky top-0 bg-white rounded-t-2xl">
                <h2 className="text-xl font-bold text-gray-900">Privacy Policy</h2>
                <button
                  type="button"
                  onClick={() => setShowPrivacyModal(false)}
                  className="p-2 rounded-lg hover:bg-gray-100 text-gray-600"
                  aria-label="Close"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="overflow-y-auto p-6 text-gray-700 text-sm leading-relaxed space-y-4">
                <p className="text-xs text-gray-500">Last updated: {new Date().toLocaleDateString()}</p>

                <section>
                  <h3 className="font-semibold text-gray-900 mb-2">1. Introduction</h3>
                  <p>African Financial Bridge Ltd (“we”, “our”, or “us”) operates this website. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our site. By using our website, you consent to the practices described here.</p>
                </section>

                <section>
                  <h3 className="font-semibold text-gray-900 mb-2">2. Information We Collect</h3>
                  <p>We may collect information you provide directly (e.g. name, email, company, phone, message content when you contact us), and automatically (e.g. IP address, browser type, device information, pages visited, and usage data through cookies and similar technologies).</p>
                </section>

                <section>
                  <h3 className="font-semibold text-gray-900 mb-2">3. How We Use Your Information</h3>
                  <p>We use collected information to respond to inquiries, improve our services and website, send relevant communications (where you have agreed), comply with legal obligations, and protect our rights and the security of our users.</p>
                </section>

                <section>
                  <h3 className="font-semibold text-gray-900 mb-2">4. Cookies and Tracking</h3>
                  <p>We may use cookies and similar technologies to remember preferences, analyze traffic, and improve user experience. You can control cookies through your browser settings.</p>
                </section>

                <section>
                  <h3 className="font-semibold text-gray-900 mb-2">5. Disclosure of Information</h3>
                  <p>We may share your information with service providers who assist our operations (under strict confidentiality), when required by law, or to protect our rights and safety. We do not sell your personal information.</p>
                </section>

                <section>
                  <h3 className="font-semibold text-gray-900 mb-2">6. Data Security</h3>
                  <p>We implement appropriate technical and organizational measures to protect your personal data against unauthorized access, alteration, disclosure, or destruction.</p>
                </section>

                <section>
                  <h3 className="font-semibold text-gray-900 mb-2">7. Your Rights</h3>
                  <p>Depending on applicable law, you may have the right to access, correct, delete, or restrict processing of your data, to object to processing, and to data portability. You may also withdraw consent or lodge a complaint with a supervisory authority.</p>
                </section>

                <section>
                  <h3 className="font-semibold text-gray-900 mb-2">8. Data Retention</h3>
                  <p>We retain your information only for as long as necessary to fulfill the purposes in this policy or as required by law.</p>
                </section>

                <section>
                  <h3 className="font-semibold text-gray-900 mb-2">9. Third-Party Links</h3>
                  <p>Our website may contain links to third-party sites. We are not responsible for their privacy practices. We encourage you to read their privacy policies.</p>
                </section>

                <section>
                  <h3 className="font-semibold text-gray-900 mb-2">10. Changes to This Policy</h3>
                  <p>We may update this Privacy Policy from time to time. The “Last updated” date at the top will reflect the latest version. Continued use of the site after changes constitutes acceptance.</p>
                </section>

                <section>
                  <h3 className="font-semibold text-gray-900 mb-2">11. Contact Us</h3>
                  <p>For privacy-related questions or to exercise your rights, contact us at financier4@yahoo.com, franciskontoh4@gmail.com, or +233 200738204. Address: PEDUASE, EASTERN REGION.</p>
                </section>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Footer; 